import { useMemo } from 'react';
import { isWithinInterval } from 'date-fns';
import type { Feriado } from '../../../models';
import type { DateRange } from './useCalendarViews';

export interface HolidaysByType {
  NACIONAL: Feriado[];
  ESTADUAL: Feriado[];
  MUNICIPAL: Feriado[];
  FOLGA: Feriado[];
}

export const useHolidayFilters = (feriados: Feriado[], dateRange: DateRange) => {
  // Filter holidays within the current date range
  const filteredHolidays = useMemo(() => {
    return feriados.filter(feriado => {
      const feriadoDate = new Date(feriado.data + 'T00:00:00');
      return isWithinInterval(feriadoDate, {
        start: dateRange.start,
        end: dateRange.end
      });
    });
  }, [feriados, dateRange]);

  // Group holidays by type
  const holidaysByType = useMemo((): HolidaysByType => {
    const grouped: HolidaysByType = {
      NACIONAL: [],
      ESTADUAL: [],
      MUNICIPAL: [],
      FOLGA: []
    };

    filteredHolidays.forEach(feriado => {
      grouped[feriado.tipo].push(feriado);
    });

    // Sort each group by date
    Object.keys(grouped).forEach(tipo => {
      grouped[tipo as keyof HolidaysByType].sort((a, b) => 
        new Date(a.data).getTime() - new Date(b.data).getTime()
      );
    });

    return grouped;
  }, [filteredHolidays]);

  // Count holidays by type
  const counts = useMemo(() => ({
    NACIONAL: holidaysByType.NACIONAL.length,
    ESTADUAL: holidaysByType.ESTADUAL.length,
    MUNICIPAL: holidaysByType.MUNICIPAL.length,
    FOLGA: holidaysByType.FOLGA.length,
    total: filteredHolidays.length
  }), [holidaysByType, filteredHolidays]);

  return {
    filteredHolidays,
    holidaysByType,
    counts
  };
};

